/**
 * SkyPass.ts — gradient sky + toon-step sun disk.
 *
 * Drawn first, no depth read/write. Subsequent passes (terrain, water) render
 * over the sky using the standard reverse-Z depth attachment.
 *
 * Reads:
 *   - Camera uniform (for invViewProj → view direction reconstruction)
 *   - LightingUniforms (sun direction)
 *   - PaletteUniforms (sky zenith / midband / horizon)
 * Writes:
 *   - RenderTarget.color (loadOp='clear' for safety, fullscreen overwrite)
 */

import skyWgsl from "../shaders/sky.wgsl?raw";
import type { Camera } from "../Camera";
import type { LightingUniforms } from "../rendering/LightingUniforms";
import type { PaletteUniforms } from "../rendering/PaletteUniforms";
import type { ManagedRenderTarget } from "../rendering/RenderTarget";
import type { FrameContext, RenderPass } from "../rendering/RenderPass";
import { createUniformBuffer } from "../gpu/Buffers";

export class SkyPass implements RenderPass {
  readonly name = "SkyPass";
  private pipeline: GPURenderPipeline;
  private bgl: GPUBindGroupLayout;
  private bindGroup: GPUBindGroup;
  private params: GPUBuffer;
  private paramsCpu = new Float32Array(4);

  constructor(
    device: GPUDevice,
    rt: ManagedRenderTarget,
    camera: Camera,
    lighting: LightingUniforms,
    palette: PaletteUniforms,
  ) {
    this.params = createUniformBuffer(device, 16, "SkyPass.params");
    const module = device.createShaderModule({
      label: "sky.wgsl",
      code: skyWgsl,
    });
    this.bgl = device.createBindGroupLayout({
      label: "SkyPass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 3,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
      ],
    });
    this.pipeline = device.createRenderPipeline({
      label: "SkyPass.pipeline",
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.bgl] }),
      vertex: { module, entryPoint: "vs_main" },
      fragment: {
        module,
        entryPoint: "fs_main",
        targets: [{ format: rt.current.colorFormat }],
      },
      primitive: { topology: "triangle-list" },
      // No depthStencil: SkyPass renders without using the depth attachment.
    });
    this.bindGroup = device.createBindGroup({
      label: "SkyPass.bg",
      layout: this.bgl,
      entries: [
        { binding: 0, resource: { buffer: camera.buffer } },
        { binding: 1, resource: { buffer: lighting.buffer } },
        { binding: 2, resource: { buffer: palette.buffer } },
        { binding: 3, resource: { buffer: this.params } },
      ],
    });
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    // Cloud drift = scene wind, scaled so a 10 m/s breeze gives a gentle pan.
    const windRad = (frame.state.windDirectionDeg * Math.PI) / 180;
    const speed = frame.state.windSpeed * (frame.state.windExposure ?? 1);
    const driftScale = 0.0018;
    this.paramsCpu[0] = frame.time;
    this.paramsCpu[1] = -Math.sin(windRad) * speed * driftScale;
    this.paramsCpu[2] = Math.cos(windRad) * speed * driftScale;
    frame.device.queue.writeBuffer(this.params, 0, this.paramsCpu);
    const pass = encoder.beginRenderPass({
      label: "SkyPass",
      colorAttachments: [
        {
          view: frame.renderTarget.colorView,
          // ClearPass already cleared color this frame; the fullscreen triangle
          // overwrites every pixel, so we just load to avoid a second clear.
          loadOp: "load",
          storeOp: "store",
        },
      ],
    });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    pass.draw(3, 1, 0, 0);
    pass.end();
  }
}
